import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { CheckCircle2, Star } from 'lucide-react';

const StormDemo = () => {
  useEffect(() => {
    document.title = 'Zeus Weather Demo - Storm Lead Generation Platform';
  }, []);

  const scrollToDemo = () => {
    const demoSection = document.getElementById('demo-calendar');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Minimal Header - Logo Only */}
      <header className="border-b border-slate-200 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <img
            src="https://customer-assets.emergentagent.com/job_roof-sales-hub/artifacts/8eyuijji_ChatGPT_Image_Jan_28__2026__08_23_42_PM-removebg-preview.png"
            alt="Zeus Weather"
            className="h-10 w-auto"
          />
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Replace $500-1,000/Month in Tools. Pay $150.
            </h1>
            <p className="text-base sm:text-xl text-slate-700 mb-8 leading-relaxed">
              Track storms, manage field teams, and feed qualified leads to your CRM—all from one platform.
            </p>
            
            <Button
              onClick={scrollToDemo}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white text-xl px-12 py-7 h-auto transition-all duration-300 hover:scale-105 shadow-xl mb-4"
            >
              Book Your 15-Minute Demo →
            </Button>
            
            <p className="text-sm text-slate-600 mt-4">
              ✓ Deployed across 20+ states • Save up to $850/month
            </p>
          </div>
        </div>
      </section>

      {/* What You'll See in Your Demo */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8 text-center">
              What You'll See in Your Demo
            </h2>
            
            <div className="space-y-4 mb-10">
              <div className="flex items-start space-x-4 p-4 rounded-lg hover:bg-slate-50 transition-colors">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-lg text-slate-800">
                    Storm tracking (replaces Hail Recon)
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 rounded-lg hover:bg-slate-50 transition-colors">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-lg text-slate-800">
                    Canvassing maps (replaces Spotio)
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 rounded-lg hover:bg-slate-50 transition-colors">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-lg text-slate-800">
                    Performance dashboards & job photos
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 rounded-lg hover:bg-slate-50 transition-colors">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-lg text-slate-800">
                    CRM integration (JobNimbus, AccuLynx)
                  </p>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <Button
                onClick={scrollToDemo}
                className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white text-lg px-10 py-6 h-auto transition-all duration-300"
              >
                Book Your 15-Minute Demo →
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Screenshot */}
      <section className="py-8 sm:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
              Your Team's Command Center
            </h2>
            <p className="text-base text-slate-700 mb-6 text-center max-w-2xl mx-auto">
              Live maps showing priority properties and damage severity. No printed lists or guesswork.
            </p>
            
            <div className="flex justify-center">
              <div className="relative w-[60%] sm:max-w-sm">
                <img
                  src="https://customer-assets.emergentagent.com/job_roof-sales-hub/artifacts/sa69a4pe_image.png"
                  alt="Zeus Weather Mobile App Interface"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-12 text-center">
              What Roofing Contractors Say
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Testimonial 1 - Chris Parker */}
              <Card className="border-2 border-slate-200 shadow-xl">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  
                  <blockquote className="text-lg text-slate-800 mb-6 leading-relaxed">
                    "Zeus alerts me when properties are affected by weather—something no other service does. I 'set it and forget it' and I'm on my way well before anyone else is aware."
                  </blockquote>
                  
                  <div className="border-t border-slate-200 pt-4">
                    <p className="font-bold text-slate-900">Chris Parker</p>
                    <p className="text-slate-600 text-sm">Storm Guard of Metro Detroit North</p>
                    <p className="text-green-700 text-sm font-semibold mt-2">First to storm sites, every time</p>
                  </div>
                </CardContent>
              </Card>

              {/* Testimonial 2 - Brad Staples */}
              <Card className="border-2 border-slate-200 shadow-xl">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  
                  <blockquote className="text-lg text-slate-800 mb-6 leading-relaxed">
                    "If Hail Recon and Spotio had a baby, this would be it... but better. Zeus combines multiple tools we use in the field, all under one roof."
                  </blockquote>
                  
                  <div className="border-t border-slate-200 pt-4">
                    <p className="font-bold text-slate-900">Brad Staples</p>
                    <p className="text-slate-600 text-sm">AQUAGUARD MITIGATION</p>
                    <p className="text-green-700 text-sm font-semibold mt-2">Replaced 3 tools, saving $600/month</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              One Platform Replaces Everything
            </h2>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border-2 border-blue-200">
              <div className="mb-6">
                <p className="text-lg text-slate-700 mb-2">Your current tools:</p>
                <p className="text-3xl font-bold text-slate-400 line-through">$500-1,000/month</p>
              </div>
              
              <div className="mb-6 pb-6 border-b-2 border-slate-200">
                <p className="text-lg text-slate-700 mb-2">Zeus:</p>
                <p className="text-5xl font-bold text-slate-900">
                  $150<span className="text-2xl text-slate-600">/month</span>
                </p>
                <p className="text-green-700 font-bold text-xl mt-3">Save $350-850/month</p>
              </div>
              
              <div className="text-left space-y-3 mb-8">
                <div className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Storm tracking (replaces Hail Recon)</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Canvassing maps (replaces Spotio)</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Performance dashboards & job site photos</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Unlimited users (no per-seat fees)</span>
                </div>
              </div>
              
              <Button
                onClick={scrollToDemo}
                className="w-full bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 h-auto transition-all duration-300"
              >
                Book Your Demo →
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8 text-center">
              Common Questions
            </h2>
            
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  How much will I save vs. my current tools?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Most roofing teams use separate tools for storm tracking, canvassing, and performance management that cost $500-1,000/month combined. Zeus replaces all of them for $150/month with unlimited users.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  Does Zeus replace my CRM?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  No. Zeus handles opportunity discovery and field execution before leads hit your CRM. It works alongside JobNimbus, AccuLynx, or whatever system you use. Zeus finds and qualifies leads. Your CRM manages jobs and closes deals.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  How fast can I start after a storm?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Storm data appears 2-4 hours after an event. Property filtering and lead generation happen instantly. Most contractors have crews in the field within 6-8 hours of a major storm—well before competitors know where it hit.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:text-blue-600">
                  What's included in the $150/month?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Everything. Unlimited storm tracking, unlimited team members, mobile field maps, performance dashboards, automated follow-ups, and full support. No hidden fees, no per-lead charges, no user limits.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Demo Calendar Section */}
      <section id="demo-calendar" className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-8">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              Stop Paying $500-1,000/Month in Tools
            </h2>
            <p className="text-lg text-slate-700 mb-2">
              Book your 15-minute demo. See exactly how Zeus replaces your current stack.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl p-4 sm:p-8">
            <iframe
              src="https://sites.leadconnectorhq.com/preview/wcmdWIrxpgWpyny0G977"
              className="w-full rounded-lg border-2 border-slate-200"
              style={{ minHeight: '700px', height: '700px' }}
              title="Book Zeus Weather Demo"
              frameBorder="0"
            />
          </div>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="bg-slate-900 text-slate-300 py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_zeus-roofer-app/artifacts/7vhqji80_logo1-removebg-preview%20%281%29.png"
            alt="Zeus Weather"
            className="h-10 w-auto mx-auto mb-4"
          />
          <p className="text-sm text-slate-400">
            &copy; 2025 Zeus Weather. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default StormDemo;
